import { componentSizes } from 'types'

import type { ComponentSize, ComponentSizeStyleProps, ComponentStyleProps } from 'types'

// TODO: For component sizes that are not supplied in the `styleProps` param,
// this defaults to the smallest provided component size. There might be a better
// approach which uses the "closest" available component size from `styleProps`.

/**
 * Creates and returns an object with keys of type `ComponentSize` and values of type
 * `StyleProps | CSSObject`. The generic type argument `TComponentParts` represents the
 * desired shape of the `StyleProps` object for each component part. This allows us to
 * restrict each component part to the props type of the Chakra component which it renders.
 *
 * Style props for each component part can then be accessed using the `size` prop from the
 * parent component and spread onto the relevant component part. This function guarantees
 * that  the returned object will contain a valid style props object for every possible value
 * of `ComponentSize`, regardless of how many are explicitly defined in the `styleProps`
 * function argument.
 *
 * This function should only be used with components which accept a `ComponentSize` prop.
 *
 * Example usage:
 *
 * ```
 * const styleProps = createSizeStyleProps<{
 *   heading?: Partial<HeadingProps>;
 * }>({
 *   sm: {
 *     heading: {
 *       color: 'primary.700',
 *     },
 *   },
 *   // ...
 * });
 * ```
 *
 * ```
 * <Heading {...styleProps[size].heading}>{teamName}</Heading>
 * ```
 */
export function createSizeStyleProps<TComponentParts extends ComponentStyleProps>(
  styleProps: ComponentSizeStyleProps<TComponentParts>
) {
  const stylePropSizes = Object.keys(styleProps) as ComponentSize[]

  return componentSizes.reduce(
    (acc, cur) => {
      return {
        ...acc,
        [cur]: styleProps[cur] || styleProps[stylePropSizes[0]] || ({} as TComponentParts),
      }
    },
    {} as Required<ComponentSizeStyleProps<TComponentParts>>
  )
}
